// Predicted Savings Forecast Widget — compact card for the Settings page.
//
// Backed by:
//   GET /api/savings/forecast/{user_id}        — 5-state machine
//   GET /api/recommendations/{user_id}/summary — list of actionable swaps
//
// The widget surfaces the same data that powers the full SavingsTracker
// dashboard, but in a friendlier teaser form designed to keep Alpha users
// engaged ahead of the Self-Learning roadmap shipping in full.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, TrendingUp, ArrowRight, Loader2 } from 'lucide-react';
import WhyThisTooltip from './WhyThisTooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// State → visual treatment + headline copy.
const STATE_META = {
  new_user: {
    gradient: 'from-slate-100 to-blue-100',
    accent: 'text-slate-700',
    chipBg: 'bg-slate-200',
    chipText: 'text-slate-700',
    label: 'Getting started',
    icon: Sparkles,
  },
  learning: {
    gradient: 'from-amber-100 to-orange-100',
    accent: 'text-amber-800',
    chipBg: 'bg-amber-200',
    chipText: 'text-amber-900',
    label: 'Learning your shop',
    icon: TrendingUp,
  },
  tracking_only: {
    gradient: 'from-emerald-100 to-teal-100',
    accent: 'text-emerald-800',
    chipBg: 'bg-emerald-200',
    chipText: 'text-emerald-900',
    label: 'Banking real savings',
    icon: TrendingUp,
  },
  optimised: {
    gradient: 'from-violet-100 to-purple-100',
    accent: 'text-violet-800',
    chipBg: 'bg-violet-200',
    chipText: 'text-violet-900',
    label: 'Already optimised',
    icon: Sparkles,
  },
  active: {
    gradient: 'from-green-100 to-emerald-100',
    accent: 'text-emerald-900',
    chipBg: 'bg-emerald-200',
    chipText: 'text-emerald-900',
    label: 'On the money',
    icon: TrendingUp,
  },
};

// Map the backend's `action` slug to a navigation target the parent handles.
const ACTION_TO_VIEW = {
  scan_first_receipt:  'receipts',
  log_shopping_trip:   'savings',
  submit_more_scans:   'compare',
  view_recommendations:'savings',
};

// Onboarding teaser — shown to brand-new users instead of the full widget.
// Fires a one-shot `forecast_teaser_shown` impression event on first render.
// Pulls A/B variant copy from /api/forecast-teaser/variant.
const ForecastOnboardingTeaser = ({ userId, onScan }) => {
  const [variant, setVariant] = useState({
    id: 'A',
    headline: 'Your first scan unlocks your savings forecast',
    subline: "We'll predict how much you can save next week — personalised to your basket.",
  });

  useEffect(() => {
    // Pull A/B variant for this user
    axios.get(`${BACKEND_URL}/api/forecast-teaser/variant`, {
      params: { user_id: userId || '' },
    }).then((r) => {
      if (r.data) {
        setVariant(r.data);
        // Stamp the impression with the resolved variant so admin
        // A/B reporting can attribute the show correctly.
        axios.post(`${BACKEND_URL}/api/analytics/event`, {
          event_type: 'forecast_teaser_shown',
          user_id: userId || 'anonymous',
          properties: { source: 'home_widget', variant: r.data.id },
        }).catch(() => {});
      }
    }).catch(() => {
      // Variant fetch failed — still log the impression (untagged).
      axios.post(`${BACKEND_URL}/api/analytics/event`, {
        event_type: 'forecast_teaser_shown',
        user_id: userId || 'anonymous',
        properties: { source: 'home_widget' },
      }).catch(() => {});
    });
    // Fire exactly once per mount.
  }, [userId]);

  const handleClick = () => {
    // Stamp the click event with the variant so A/B can be measured.
    axios.post(`${BACKEND_URL}/api/analytics/event`, {
      event_type: 'forecast_teaser_clicked',
      user_id: userId || 'anonymous',
      properties: { source: 'home_widget', variant: variant.id },
    }).catch(() => {});
    if (onScan) onScan();
  };

  return (
    <section
      className="rounded-2xl border border-dashed border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 px-4 py-3 mb-5 flex items-center gap-3"
      data-testid="savings-forecast-onboarding-teaser"
      data-variant={variant.id}
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white border border-green-200 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-green-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-green-900 leading-tight">
          {variant.headline}
        </div>
        <div className="text-[11px] text-green-700/80 mt-0.5">
          {variant.subline}
        </div>
      </div>
      <button
        type="button"
        onClick={handleClick}
        className="flex-shrink-0 text-xs font-semibold text-green-700 hover:text-green-800 inline-flex items-center gap-1 group"
        data-testid="savings-forecast-onboarding-cta"
      >
        Scan
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </button>
    </section>
  );
};

const SavingsForecastWidget = ({ userId, userData, onNavigate }) => {
  const [forecast, setForecast] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const [f, s, d] = await Promise.allSettled([
          axios.get(`${BACKEND_URL}/api/savings/forecast/${userId}`),
          axios.get(`${BACKEND_URL}/api/recommendations/${userId}/summary`),
          axios.get(`${BACKEND_URL}/api/savings/dashboard/${userId}`),
        ]);
        if (cancelled) return;
        if (f.status === 'fulfilled') setForecast(f.value.data);
        if (s.status === 'fulfilled') setSummary(s.value.data);
        if (d.status === 'fulfilled') setDashboard(d.value.data);
        if (f.status === 'rejected') setError('forecast');
      } catch (e) {
        if (!cancelled) setError('network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <section
        className="rounded-2xl border border-gray-200 bg-white px-4 py-3 mb-5"
        data-testid="savings-forecast-widget-loading"
      >
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading your savings forecast…</span>
        </div>
      </section>
    );
  }

  if (error || !forecast) {
    // Silent skip — never gate Settings on a Phase 7 outage.
    return null;
  }

  // Onboarding gate — for brand-new sign-ups (no realised history, no recs,
  // no submissions) we show a tiny conversion teaser instead of the full
  // widget. Turns the gated state into a clear "earn it" nudge.
  const totalScans = Number(userData?.approved_submissions || userData?.total_submissions || 0);
  if (forecast.forecast_state === 'new_user' && totalScans === 0) {
    return (
      <ForecastOnboardingTeaser
        userId={userId}
        onScan={() => {
          if (onNavigate) onNavigate('compare');
        }}
      />
    );
  }

  const state = forecast.forecast_state || 'new_user';
  const meta = STATE_META[state] || STATE_META.new_user;
  const Icon = meta.icon;
  const predicted = Number(forecast.predicted_weekly_savings || 0);
  const realised = Number(forecast.realised_last_7d_savings || 0);
  const accuracy = forecast.accuracy_percentage;
  const ctaHeadline = forecast.cta?.headline || 'Your savings forecast';
  const ctaSubline  = forecast.cta?.subline  || '';
  const actionSlug  = forecast.cta?.action;
  const targetView  = ACTION_TO_VIEW[actionSlug] || 'savings';
  const recCount = Number(summary?.total_recommendations || 0);
  const lifetimeSavings = Number(dashboard?.lifetime_savings || 0);
  const showLifetimeBadge = state === 'optimised' && lifetimeSavings > 0;

  const handleCta = () => {
    if (onNavigate) onNavigate(targetView);
  };

  return (
    <section
      className="rounded-2xl border border-gray-200 overflow-hidden mb-5"
      data-testid="savings-forecast-widget"
      data-state={state}
    >
      <div className={`bg-gradient-to-br ${meta.gradient} px-4 py-4`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
              <span
                className={`text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full ${meta.chipBg} ${meta.chipText}`}
                data-testid="savings-forecast-state-chip"
              >
                {meta.label}
              </span>
            </div>

            <h3
              className={`text-base sm:text-lg font-bold ${meta.accent} leading-tight`}
              data-testid="savings-forecast-headline"
            >
              {ctaHeadline}
            </h3>

            {ctaSubline && (
              <p
                className="mt-1.5 text-xs text-gray-700/90 leading-relaxed line-clamp-2"
                data-testid="savings-forecast-subline"
              >
                {ctaSubline}
              </p>
            )}

            {showLifetimeBadge && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 backdrop-blur-sm border border-violet-200 text-[11px] font-semibold text-violet-800 shadow-sm"
                data-testid="savings-forecast-lifetime-badge"
              >
                <Sparkles className="w-3 h-3" />
                You've saved £{lifetimeSavings.toFixed(2)} so far
              </div>
            )}
          </div>
        </div>

        {/* Stat row — only show when we actually have non-zero data */}
        {(predicted > 0 || realised > 0 || recCount > 0) && (
          <div
            className="mt-3 grid grid-cols-3 gap-2 text-center"
            data-testid="savings-forecast-stats"
          >
            <div className="rounded-lg bg-white/70 backdrop-blur-sm border border-white px-2 py-2">
              <div className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">
                Predicted / wk
              </div>
              <div
                className="text-sm font-bold text-gray-900"
                data-testid="savings-forecast-predicted"
              >
                £{predicted.toFixed(2)}
              </div>
            </div>
            <div className="rounded-lg bg-white/70 backdrop-blur-sm border border-white px-2 py-2">
              <div className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">
                Banked (7d)
              </div>
              <div
                className="text-sm font-bold text-gray-900"
                data-testid="savings-forecast-realised"
              >
                £{realised.toFixed(2)}
              </div>
            </div>
            <div className="rounded-lg bg-white/70 backdrop-blur-sm border border-white px-2 py-2">
              <div className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">
                {accuracy !== null && accuracy !== undefined ? 'Accuracy' : 'Swaps ready'}
              </div>
              <div
                className="text-sm font-bold text-gray-900"
                data-testid="savings-forecast-secondary-stat"
              >
                {accuracy !== null && accuracy !== undefined
                  ? `${Number(accuracy).toFixed(0)}%`
                  : recCount}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white px-4 py-2.5 flex items-center justify-between gap-3">
        {recCount > 0 ? (
          <WhyThisTooltip userId={userId} label="Why this forecast?" testid="savings-forecast-why-this" />
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleCta}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 hover:text-green-800 transition group"
          data-testid="savings-forecast-cta"
        >
          See details
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </section>
  );
};

export default SavingsForecastWidget;
